/**
 * @file components/settings/CropImportModal.tsx
 * @description 作物字典「上傳一覽表並 AI 識別匯入」彈窗（序157）。
 *
 * 流程：上傳 xlsx/docx → 後端 AI 識別回傳預覽陣列（不寫庫）→
 * 使用者勾選/修正欄位 → 確認匯入（逐筆建立，允許部分失敗，回傳成功/失敗筆數）。
 */
import { useState } from 'react';
import { Alert, Button, Input, Modal, Select, Table, Typography, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { message } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { UploadCloud } from 'lucide-react';
import type { CreateCropInput, CropMainCategory, CropScheme } from '@zhcb/shared';
import { CROP_MAIN_CATEGORIES_TAP, CROP_MAIN_CATEGORIES_TOP } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';

/** 預覽列（識別結果 + 前端本地 key，尚未落庫） */
interface PreviewRow extends CreateCropInput {
  key: number;
}

interface Props {
  open: boolean;
  /** 匯入完成後回填的預設方案（沿用父層目前選中的 Tab，僅影響識別失敗時的兜底值） */
  defaultScheme: CropScheme;
  onClose: () => void;
  /** 匯入至少 1 筆成功後呼叫，讓父層刷新列表 */
  onImported: () => void;
}

const MAIN_CATEGORY_OPTIONS: Record<CropScheme, readonly string[]> = {
  TOP: CROP_MAIN_CATEGORIES_TOP,
  TAP: CROP_MAIN_CATEGORIES_TAP,
};

export function CropImportModal({ open, defaultScheme, onClose, onImported }: Props) {
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);
  const [resultSummary, setResultSummary] = useState<{ succeeded: number; failed: number } | null>(
    null,
  );

  const reset = () => {
    setRows([]);
    setSelectedKeys([]);
    setResultSummary(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const patchRow = (key: number, patch: Partial<PreviewRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const uploadProps: UploadProps = {
    accept: '.xlsx,.docx',
    showUploadList: false,
    disabled: uploading,
    beforeUpload: (file) => {
      void (async () => {
        setUploading(true);
        setResultSummary(null);
        try {
          const uploaded = await uploadFile(file, { scope: 'misc' });
          const res = await apiClient.post<{ items: CreateCropInput[] }>(
            '/api/settings/crops/recognize',
            { fileId: uploaded.id },
          );
          if (!res.items.length) {
            message.warning('未從文件中識別出任何作物項目，請確認文件內容或格式');
          }
          setRows(
            res.items.map((it, i) => ({
              ...it,
              scheme: it.scheme ?? defaultScheme,
              key: i,
            })),
          );
          setSelectedKeys(res.items.map((_, i) => i));
        } catch (e) {
          message.error((e as Error).message || '識別失敗，請確認文件格式（僅支援 .xlsx / .docx）');
        } finally {
          setUploading(false);
        }
      })();
      return false; // 阻止 antd Upload 預設的自動上傳行為，改由上方手動流程接管
    },
  };

  const confirmImport = async () => {
    const selected = rows.filter((r) => selectedKeys.includes(r.key));
    if (!selected.length) {
      message.warning('請至少勾選一筆要匯入的項目');
      return;
    }
    setImporting(true);
    try {
      const payload: CreateCropInput[] = selected.map((r) => ({
        scheme: r.scheme,
        mainCategory: r.mainCategory,
        subCategory: r.subCategory,
        itemGroup: r.itemGroup,
        name: r.name,
        productScope: r.productScope,
        remark: r.remark,
        isActive: r.isActive,
        sortOrder: r.sortOrder,
      }));
      const res = await apiClient.post<{ succeeded: number; failed: number }>(
        '/api/settings/crops/import',
        { items: payload },
      );
      setResultSummary(res);
      if (res.succeeded > 0) onImported();
      if (res.failed === 0) {
        message.success(`已成功匯入 ${res.succeeded} 筆`);
        handleClose();
      }
    } catch (e) {
      message.error((e as Error).message || '匯入失敗');
    } finally {
      setImporting(false);
    }
  };

  const columns: ColumnsType<PreviewRow> = [
    {
      title: '方案',
      dataIndex: 'scheme',
      width: 110,
      render: (v: CropScheme, row) => (
        <Select
          size="small"
          value={v}
          style={{ width: 100 }}
          options={[
            { label: '有機 TOP', value: 'TOP' },
            { label: '產銷 TAP', value: 'TAP' },
          ]}
          onChange={(next: CropScheme) =>
            patchRow(row.key, {
              scheme: next,
              mainCategory: (MAIN_CATEGORY_OPTIONS[next][0] ?? '') as CropMainCategory,
            })
          }
        />
      ),
    },
    {
      title: '產品類別',
      dataIndex: 'mainCategory',
      width: 140,
      render: (v: string, row) => (
        <Select
          size="small"
          value={v}
          style={{ width: 130 }}
          options={MAIN_CATEGORY_OPTIONS[row.scheme].map((c) => ({
            label: c,
            value: c,
          }))}
          onChange={(next: string) => patchRow(row.key, { mainCategory: next as CropMainCategory })}
        />
      ),
    },
    {
      title: '次要類別',
      dataIndex: 'subCategory',
      width: 130,
      render: (v: string, row) => (
        <Input
          size="small"
          value={v}
          onChange={(e) => patchRow(row.key, { subCategory: e.target.value })}
        />
      ),
    },
    {
      title: '品項組',
      dataIndex: 'itemGroup',
      width: 120,
      render: (v: string | null, row) => (
        <Input
          size="small"
          value={v ?? ''}
          placeholder="可留空"
          onChange={(e) => patchRow(row.key, { itemGroup: e.target.value || null })}
        />
      ),
    },
    {
      title: '產品品項',
      dataIndex: 'name',
      width: 150,
      render: (v: string, row) => (
        <Input
          size="small"
          value={v}
          onChange={(e) => patchRow(row.key, { name: e.target.value })}
        />
      ),
    },
    {
      title: '產品範圍',
      dataIndex: 'productScope',
      width: 150,
      render: (v: string | null, row) => (
        <Input
          size="small"
          value={v ?? ''}
          placeholder="僅 TOP 使用"
          onChange={(e) => patchRow(row.key, { productScope: e.target.value || null })}
        />
      ),
    },
    {
      title: '備註',
      dataIndex: 'remark',
      width: 180,
      render: (v: string | null, row) => (
        <Input
          size="small"
          value={v ?? ''}
          onChange={(e) => patchRow(row.key, { remark: e.target.value || null })}
        />
      ),
    },
  ];

  return (
    <Modal
      title="上傳作物一覽表並 AI 識別匯入"
      open={open}
      onCancel={handleClose}
      width={960}
      footer={
        rows.length
          ? [
              <Button key="cancel" onClick={handleClose}>
                取消
              </Button>,
              <Button
                key="confirm"
                type="primary"
                loading={importing}
                onClick={() => void confirmImport()}
              >
                確認匯入（已選 {selectedKeys.length} 筆）
              </Button>,
            ]
          : null
      }
    >
      {!rows.length ? (
        <Upload.Dragger {...uploadProps}>
          <p style={{ fontSize: 32, color: '#3f6b4a' }}>
            <UploadCloud size={32} />
          </p>
          <p>{uploading ? '識別中，請稍候…' : '點擊或拖曳上傳作物一覽表'}</p>
          <p style={{ color: '#8c8c8c', fontSize: 12 }}>
            僅支援 .xlsx（Excel）或 .docx（Word）格式；識別結果會先預覽，確認後才會建立資料。
          </p>
        </Upload.Dragger>
      ) : (
        <>
          {resultSummary && (
            <Alert
              style={{ marginBottom: 12 }}
              type={resultSummary.failed ? 'warning' : 'success'}
              message={`匯入完成：成功 ${resultSummary.succeeded} 筆，失敗 ${resultSummary.failed} 筆`}
              description={
                resultSummary.failed
                  ? '請檢查失敗項目是否有重複或欄位不符規則，修正後可重新整批送出。'
                  : undefined
              }
            />
          )}
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            共識別出 {rows.length} 筆，可勾選要匯入的項目並直接在表格內修正欄位。
          </Typography.Paragraph>
          <Table<PreviewRow>
            rowKey="key"
            size="small"
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content', y: 420 }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys as number[]),
            }}
          />
        </>
      )}
    </Modal>
  );
}
